/**
 * Custom Form Builder
 * Handles video backgrounds, form validation, filtered dropdowns, and form submission
 */

if (typeof CustomFormBuilder === 'undefined') {
  window.CustomFormBuilder = class CustomFormBuilder {
  constructor(formElement) {
    this.form = formElement;
    this.formSection = this.form.closest('.custom-form-builder');
    this.messagesContainer = this.form.querySelector('[data-form-messages]');
    this.submitButton = this.form.querySelector('button[type="submit"]');
    this.fieldsContainer = this.form.querySelector('.custom-form-builder__fields');
    this.successContainer = this.form.querySelector('[data-form-success]');

    // Success message settings
    this.successMode = this.form.dataset.successMode || 'default';
    this.successPopoverId = this.form.dataset.successPopoverId;

    // Validation patterns
    this.validators = {
      email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      phone: /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
      date: /^\d{4}-\d{2}-\d{2}$/,
      time: /^\d{2}:\d{2}(:\d{2})?$/,
      datetime: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/
    };

    // Error messages
    this.errorMessages = {
      required: 'This field is required',
      email: 'Please enter a valid email address',
      phone: 'Please enter a valid phone number',
      min_length: 'Minimum {count} characters required',
      date: 'Please enter a valid date',
      time: 'Please enter a valid time',
      datetime: 'Please enter a valid date and time',
      date_min: 'Date must be on or after {date}',
      date_max: 'Date must be on or before {date}',
      time_min: 'Time must be at or after {time}',
      time_max: 'Time must be at or before {time}',
      datetime_min: 'Date/time must be on or after {datetime}',
      datetime_max: 'Date/time must be on or before {datetime}'
    };

    this.init();
  }

  init() {
    this.populateDropdowns();
    this.setupFilteredDropdowns();
    this.setupValidation();
    this.setupSubmission();
    this.setupSuccessReset();
    this.setupImageUpload();
  }

  /**
   * Populate regular dropdowns from JSON data
   */
  populateDropdowns() {
    const dropdowns = this.form.querySelectorAll('select:not([data-filtered-dropdown])');

    dropdowns.forEach(dropdown => {
      const blockId = dropdown.id.replace('Field-', '');
      const optionsScript = this.form.querySelector(`[data-dropdown-options="${blockId}"]`);

      if (optionsScript) {
        try {
          const options = JSON.parse(optionsScript.textContent);

          // Clear existing options except first (placeholder)
          while (dropdown.options.length > 1) {
            dropdown.remove(1);
          }

          // Add options from JSON
          options.forEach(option => {
            const opt = document.createElement('option');
            opt.value = option.value;
            opt.textContent = option.label;
            dropdown.appendChild(opt);
          });
        } catch (error) {
          console.error('Error parsing dropdown options:', error);
        }
      }
    });
  }

  /**
   * Setup filtered dropdowns that depend on other field values
   */
  setupFilteredDropdowns() {
    const filteredDropdowns = this.form.querySelectorAll('[data-filtered-dropdown]');

    filteredDropdowns.forEach(dropdown => {
      const dependsOn = dropdown.dataset.dependsOn;
      const blockId = dropdown.id.replace('Field-', '');
      const optionsScript = this.form.querySelector(`[data-filtered-options="${blockId}"]`);

      if (!dependsOn || !optionsScript) return;

      try {
        const filteredOptions = JSON.parse(optionsScript.textContent);

        // Find the parent field by name attribute
        const parentField = this.form.querySelector(`[name="${dependsOn}"]`);

        if (parentField) {
          // Listen for changes on parent field
          parentField.addEventListener('change', (e) => {
            const parentValue = e.target.value;
            this.updateFilteredDropdown(dropdown, parentValue, filteredOptions);
          });

          // Initialize filtered dropdown based on current parent value
          if (parentField.value) {
            this.updateFilteredDropdown(dropdown, parentField.value, filteredOptions);
          }
        }
      } catch (error) {
        console.error('Error setting up filtered dropdown:', error);
      }
    });
  }

  /**
   * Update filtered dropdown options based on parent value
   */
  updateFilteredDropdown(dropdown, parentValue, filteredOptions) {
    // Clear current options except placeholder
    while (dropdown.options.length > 1) {
      dropdown.remove(1);
    }

    // Reset dropdown value
    dropdown.value = '';

    // If parent has no value, disable dropdown
    if (!parentValue) {
      dropdown.disabled = true;
      return;
    }

    // Enable dropdown
    dropdown.disabled = false;

    // Get options for this parent value
    const options = filteredOptions[parentValue] || [];

    // Add filtered options
    options.forEach(option => {
      const opt = document.createElement('option');
      opt.value = option.value;
      opt.textContent = option.label;
      dropdown.appendChild(opt);
    });

    // If no options available, show message
    if (options.length === 0) {
      const opt = document.createElement('option');
      opt.value = '';
      opt.textContent = 'No options available';
      opt.disabled = true;
      dropdown.appendChild(opt);
    }
  }

  /**
   * Setup real-time validation on form fields
   */
  setupValidation() {
    const fields = this.form.querySelectorAll('input, textarea, select');

    fields.forEach(field => {
      // Validate on blur
      field.addEventListener('blur', () => {
        this.validateField(field);
      });

      // Clear error on input
      field.addEventListener('input', () => {
        this.clearFieldError(field);
      });
    });
  }

  /**
   * Validate a single field
   */
  validateField(field) {
    const value = field.value.trim();
    const validationType = field.dataset.validation;
    const isRequired = field.hasAttribute('required');
    const minLength = parseInt(field.dataset.minLength) || 0;

    let errorMessage = '';

    // Check required
    if (isRequired && !value) {
      errorMessage = this.errorMessages.required;
    }
    // Check validation type
    else if (value && validationType) {
      switch (validationType) {
        case 'email':
          if (!this.validators.email.test(value)) {
            errorMessage = this.errorMessages.email;
          }
          break;

        case 'phone':
          if (!this.validators.phone.test(value)) {
            errorMessage = this.errorMessages.phone;
          }
          break;

        case 'min_length':
          if (value.length < minLength) {
            errorMessage = this.errorMessages.min_length.replace('{count}', minLength);
          }
          break;

        case 'date':
          if (!this.validators.date.test(value)) {
            errorMessage = this.errorMessages.date;
          } else {
            // Check min/max date constraints
            const dateValue = new Date(value);
            const minDate = field.getAttribute('min');
            const maxDate = field.getAttribute('max');
            
            if (minDate && dateValue < new Date(minDate)) {
              errorMessage = this.errorMessages.date_min.replace('{date}', this.formatDate(minDate));
            } else if (maxDate && dateValue > new Date(maxDate)) {
              errorMessage = this.errorMessages.date_max.replace('{date}', this.formatDate(maxDate));
            }
          }
          break;

        case 'time':
          if (!this.validators.time.test(value)) {
            errorMessage = this.errorMessages.time;
          } else {
            // Check min/max time constraints
            const minTime = field.getAttribute('min');
            const maxTime = field.getAttribute('max');
            
            if (minTime && value < minTime) {
              errorMessage = this.errorMessages.time_min.replace('{time}', this.formatTime(minTime));
            } else if (maxTime && value > maxTime) {
              errorMessage = this.errorMessages.time_max.replace('{time}', this.formatTime(maxTime));
            }
          }
          break;

        case 'datetime':
          if (!this.validators.datetime.test(value)) {
            errorMessage = this.errorMessages.datetime;
          } else {
            // Check min/max datetime constraints
            const datetimeValue = new Date(value);
            const minDatetime = field.getAttribute('min');
            const maxDatetime = field.getAttribute('max');
            
            if (minDatetime && datetimeValue < new Date(minDatetime)) {
              errorMessage = this.errorMessages.datetime_min.replace('{datetime}', this.formatDateTime(minDatetime));
            } else if (maxDatetime && datetimeValue > new Date(maxDatetime)) {
              errorMessage = this.errorMessages.datetime_max.replace('{datetime}', this.formatDateTime(maxDatetime));
            }
          }
          break;
      }
    }
    // Check min_length even without validation type
    else if (value && minLength > 0 && value.length < minLength) {
      errorMessage = this.errorMessages.min_length.replace('{count}', minLength);
    }

    if (errorMessage) {
      this.showFieldError(field, errorMessage);
      return false;
    } else {
      this.clearFieldError(field);
      return true;
    }
  }

  /**
   * Format date for display in error messages
   */
  formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  /**
   * Format time for display in error messages
   */
  formatTime(timeString) {
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }

  /**
   * Format datetime for display in error messages
   */
  formatDateTime(datetimeString) {
    const date = new Date(datetimeString);
    return date.toLocaleString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  /**
   * Show error message for a field
   */
  showFieldError(field, message) {
    field.classList.add('error');
    field.setAttribute('aria-invalid', 'true');

    const errorContainer = field.parentElement.querySelector('.field__error');
    if (errorContainer) {
      const errorMessage = errorContainer.querySelector('.error-message');
      if (errorMessage) {
        errorMessage.textContent = message;
      }
      errorContainer.style.display = 'block';
      field.setAttribute('aria-describedby', errorContainer.id);
    }
  }

  /**
   * Clear error message for a field
   */
  clearFieldError(field) {
    field.classList.remove('error');
    field.removeAttribute('aria-invalid');
    field.removeAttribute('aria-describedby');

    const errorContainer = field.parentElement.querySelector('.field__error');
    if (errorContainer) {
      errorContainer.style.display = 'none';
      const errorMessage = errorContainer.querySelector('.error-message');
      if (errorMessage) {
        errorMessage.textContent = '';
      }
    }
  }

  /**
   * Validate entire form
   */
  validateForm() {
    const fields = this.form.querySelectorAll('input, textarea, select');
    let isValid = true;

    fields.forEach(field => {
      // Skip submit buttons
      if (field.type === 'submit') return;

      if (!this.validateField(field)) {
        isValid = false;
      }
    });

    return isValid;
  }

  /**
   * Setup form submission
   */
  setupSubmission() {
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();

      // Validate form
      if (!this.validateForm()) {
        this.showMessage('Please enter all the fields above', 'error');
        return;
      }

      // Collect form data
      const formData = this.collectFormData();

      // Submit form
      this.submitForm(formData);
    });
  }

  /**
   * Collect form data as JSON object
   */
  collectFormData() {
    const data = {};
    const fields = this.form.querySelectorAll('input, textarea, select');

    fields.forEach(field => {
      // Skip submit buttons and fields without name
      if (field.type === 'submit' || !field.name) return;

      data[field.name] = field.value;
    });

    return data;
  }

  /**
   * Submit form data
   */
  async submitForm(data) {
    const url = this.form.action;
    const method = this.form.method.toUpperCase();

    console.log('[Form] Submitting to:', url);
    console.log('[Form] Method:', method);
    console.log('[Form] Data:', data);

    // Show loading state
    this.form.classList.add('loading');
    if (this.submitButton) {
      this.submitButton.disabled = true;
    }

    try {
      // Use FormData instead of JSON for Shopify compatibility
      const formData = new FormData(this.form);
      
      const response = await fetch(url, {
        method: method,
        body: formData
      });

      console.log('[Form] Response status:', response.status);
      console.log('[Form] Response ok:', response.ok);
      console.log('[Form] Response headers:', response.headers);

      if (response.ok) {
        console.log('[Form] Success!');
        this.handleSuccess(response);
      } else {
        // Try to get error details from response
        let errorMessage = 'There was an error submitting the form. Please try again.';
        try {
          const errorData = await response.text();
          console.error('[Form] Error response:', errorData);
          
          // Try to parse JSON error
          try {
            const jsonError = JSON.parse(errorData);
            if (jsonError.message) {
              errorMessage = jsonError.message;
            } else if (jsonError.error) {
              errorMessage = jsonError.error;
            }
          } catch (e) {
            // Not JSON, check if HTML contains error message
            if (errorData.includes('error')) {
              errorMessage = `Submission failed (Status: ${response.status})`;
            }
          }
        } catch (e) {
          errorMessage = `Submission failed with status ${response.status}`;
        }
        
        console.error('[Form] Submission failed with status:', response.status);
        this.showMessage(errorMessage, 'error');
      }
    } catch (error) {
      console.error('[Form] Submission error:', error);
      
      // Show the actual error message from the exception
      const errorMessage = error.message || 'There was an error submitting the form. Please try again.';
      this.showMessage(errorMessage, 'error');
    } finally {
      // Remove loading state
      this.form.classList.remove('loading');
      if (this.submitButton) {
        this.submitButton.disabled = false;
      }
    }
  }

  /**
   * Handle successful form submission
   */
  handleSuccess(response) {
    // Handle different success modes
    switch (this.successMode) {
      case 'popover':
        this.triggerSuccessPopover();
        this.resetForm();
        break;

      case 'inline':
        this.showInlineSuccess();
        break;

      case 'default':
      default:
        this.showMessage('Form submitted successfully!', 'success');
        this.resetForm();
        break;
    }
  }

  /**
   * Trigger success popover
   */
  triggerSuccessPopover() {
    if (!this.successPopoverId) {
      console.warn('Success popover ID not specified');
      this.showMessage('Form submitted successfully!', 'success');
      return;
    }

    // Find popover by ID
    const popover = document.querySelector(`[data-popover-id="${this.successPopoverId}"]`);

    if (popover) {
      // Trigger the popover
      popover.dispatchEvent(new CustomEvent('trigger-popover', {
        detail: {
          source: 'form-success',
          formId: this.form.id,
          triggerElement: this.submitButton
        }
      }));
    } else {
      console.warn(`Popover with ID "${this.successPopoverId}" not found`);
      this.showMessage('Form submitted successfully!', 'success');
    }
  }

  /**
   * Show inline success message
   */
  showInlineSuccess() {
    if (!this.successContainer) {
      console.warn('Inline success container not found');
      this.showMessage('Form submitted successfully!', 'success');
      this.resetForm();
      return;
    }

    // Hide form fields
    if (this.fieldsContainer) {
      this.fieldsContainer.style.display = 'none';
    }

    // Hide submit button if it's outside fields container
    if (this.submitButton && !this.fieldsContainer?.contains(this.submitButton)) {
      this.submitButton.style.display = 'none';
    }

    // Show success container (remove hidden attribute and set display)
    this.successContainer.removeAttribute('hidden');
    this.successContainer.style.display = 'block';

    // Scroll to success message
    this.successContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /**
   * Reset form to initial state
   */
  resetForm() {
    this.form.reset();

    // Reset filtered dropdowns
    const filteredDropdowns = this.form.querySelectorAll('[data-filtered-dropdown]');
    filteredDropdowns.forEach(dropdown => {
      if (dropdown && dropdown.options) {
        dropdown.disabled = true;
        while (dropdown.options.length > 1) {
          dropdown.remove(1);
        }
      }
    });

    // Clear all field errors
    const fields = this.form.querySelectorAll('input, textarea, select');
    fields.forEach(field => {
      this.clearFieldError(field);
    });
  }

  /**
   * Setup success reset button
   */
  setupSuccessReset() {
    const resetButton = this.form.querySelector('[data-form-reset]');
    if (!resetButton) return;

    resetButton.addEventListener('click', () => {
      // Hide success message
      if (this.successContainer) {
        this.successContainer.hidden = true;
      }

      // Show form fields
      if (this.fieldsContainer) {
        this.fieldsContainer.hidden = false;
      }

      // Show submit button
      if (this.submitButton) {
        this.submitButton.hidden = false;
      }

      // Reset form
      this.resetForm();

      // Scroll to form
      this.form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /**
   * Show success or error message
   */
  showMessage(message, type) {
    if (!this.messagesContainer) return;

    this.messagesContainer.textContent = message;
    this.messagesContainer.className = `custom-form-builder__messages ${type}`;

    // Auto-hide after 5 seconds
    setTimeout(() => {
      this.messagesContainer.className = 'custom-form-builder__messages';
      this.messagesContainer.textContent = '';
    }, 5000);
  }

  /**
   * Setup image upload functionality
   */
  setupImageUpload() {
    const dropzones = this.form.querySelectorAll('[data-file-dropzone]');
    
    dropzones.forEach(dropzone => {
      const input = dropzone.querySelector('[data-file-input]');
      const content = dropzone.querySelector('[data-file-content]');
      const preview = dropzone.querySelector('[data-file-preview]');
      const previewImage = dropzone.querySelector('[data-preview-image]');
      const previewName = dropzone.querySelector('[data-preview-name]');
      const previewSize = dropzone.querySelector('[data-preview-size]');
      const removeBtn = dropzone.querySelector('[data-file-remove]');

      if (!input) return;

      const maxSize = parseInt(input.dataset.maxSize) * 1024 * 1024; // Convert MB to bytes

      // Handle file selection
      input.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (file) {
          this.handleFileUpload(file, maxSize, {
            dropzone,
            content,
            preview,
            previewImage,
            previewName,
            previewSize,
            input
          });
        }
      });

      // Drag and drop handlers
      dropzone.addEventListener('dragover', (e) => {
        e.preventDefault();
        dropzone.classList.add('dragover');
      });

      dropzone.addEventListener('dragleave', () => {
        dropzone.classList.remove('dragover');
      });

      dropzone.addEventListener('drop', (e) => {
        e.preventDefault();
        dropzone.classList.remove('dragover');
        
        const file = e.dataTransfer.files[0];
        if (file) {
          // Set the file to the input
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(file);
          input.files = dataTransfer.files;
          
          this.handleFileUpload(file, maxSize, {
            dropzone,
            content,
            preview,
            previewImage,
            previewName,
            previewSize,
            input
          });
        }
      });

      // Remove file handler
      if (removeBtn) {
        removeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          input.value = '';
          content.hidden = false;
          preview.hidden = true;
          previewImage.src = '';
        });
      }
    });
  }

  /**
   * Handle file upload validation and preview
   */
  handleFileUpload(file, maxSize, elements) {
    const { dropzone, content, preview, previewImage, previewName, previewSize, input } = elements;

    // Validate file size
    if (file.size > maxSize) {
      this.showMessage(`File size must be less than ${maxSize / (1024 * 1024)}MB`, 'error');
      input.value = '';
      return;
    }

    // Validate file type
    const acceptedTypes = input.accept.split(',').map(type => type.trim());
    const isValidType = acceptedTypes.some(type => {
      if (type === 'image/*') return file.type.startsWith('image/');
      if (type.startsWith('.')) return file.name.toLowerCase().endsWith(type.toLowerCase());
      return file.type === type;
    });

    if (!isValidType) {
      this.showMessage('Invalid file type', 'error');
      input.value = '';
      return;
    }

    // Show preview
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        previewImage.src = e.target.result;
        previewName.textContent = file.name;
        previewSize.textContent = this.formatFileSize(file.size);
        content.hidden = true;
        preview.hidden = false;
      };
      reader.readAsDataURL(file);
    } else {
      // For non-image files, just show file info
      previewImage.style.display = 'none';
      previewName.textContent = file.name;
      previewSize.textContent = this.formatFileSize(file.size);
      content.hidden = true;
      preview.hidden = false;
    }
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }
  };
}

/**
 * Initialize all form builders on page load
 */
document.addEventListener('DOMContentLoaded', () => {
  // Initialize all custom form builders
  const forms = document.querySelectorAll('[data-form-builder]');
  forms.forEach(form => {
    new CustomFormBuilder(form);
  });
});

/**
 * Shopify section editor support
 */
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    const form = section.querySelector('[data-form-builder]');

    if (form) {
      new CustomFormBuilder(form);
    }
  });
}
